import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Briefcase, MapPin, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Careers = () => {
  const navigate = useNavigate();

  const jobs = [
    {
      title: 'Senior Full Stack Developer',
      department: 'Engineering',
      location: 'Mumbai, India',
      type: 'Full-time',
      description: 'Join our engineering team to build scalable solutions for our growing platform.'
    },
    {
      title: 'Product Manager',
      department: 'Product',
      location: 'Bangalore, India',
      type: 'Full-time',
      description: 'Lead product strategy and development for our home services marketplace.'
    },
    {
      title: 'Customer Success Manager',
      department: 'Operations',
      location: 'Delhi, India',
      type: 'Full-time',
      description: 'Help our customers get the most out of doorush services.'
    },
    {
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Remote',
      type: 'Full-time',
      description: 'Design beautiful and intuitive experiences for our mobile and web applications.'
    },
    {
      title: 'Business Development Executive',
      department: 'Sales',
      location: 'Pune, India',
      type: 'Full-time',
      description: 'Expand our service provider network and grow our business in new cities.'
    },
    {
      title: 'Marketing Specialist',
      department: 'Marketing',
      location: 'Mumbai, India',
      type: 'Full-time',
      description: 'Create and execute marketing campaigns to grow our customer base.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png" 
                alt="doorush logo" 
                className="h-10 w-auto"
              />
            </div>
            <Button onClick={() => navigate('/')} variant="outline" className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white">
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007e7a]/5 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Join Our Team
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Help us revolutionize home services in India. Be part of a team that's making a difference in millions of lives.
          </p>
        </div>
      </section>

      {/* Jobs Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Open Positions</h2>
          <div className="space-y-6">
            {jobs.map((job, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-2xl font-bold text-gray-900 mb-2">{job.title}</CardTitle>
                      <div className="flex flex-wrap gap-3 text-gray-600">
                        <div className="flex items-center gap-1">
                          <Briefcase size={16} />
                          <span className="text-sm">{job.department}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin size={16} />
                          <span className="text-sm">{job.location}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span className="text-sm">{job.type}</span>
                        </div>
                      </div>
                    </div>
                    <Button className="bg-[#007e7a] hover:bg-[#006666] text-white">
                      Apply Now
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{job.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Perks Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Why Work at doorush?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Competitive Salary</h3>
                <p className="text-gray-600">Industry-leading compensation packages with performance bonuses.</p>
              </CardContent>
            </Card>
            <Card className="p-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Health Benefits</h3>
                <p className="text-gray-600">Comprehensive health insurance for you and your family.</p>
              </CardContent>
            </Card>
            <Card className="p-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Flexible Work</h3>
                <p className="text-gray-600">Remote work options and flexible working hours.</p>
              </CardContent>
            </Card>
            <Card className="p-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Learning & Growth</h3>
                <p className="text-gray-600">Continuous learning opportunities and career development programs.</p>
              </CardContent>
            </Card>
            <Card className="p-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Team Events</h3>
                <p className="text-gray-600">Regular team outings, celebrations, and fun activities.</p>
              </CardContent>
            </Card>
            <Card className="p-6">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Impact</h3>
                <p className="text-gray-600">Work on products that make a real difference in people's lives.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;